import type { $Fetch } from "ofetch";
/**
 * Nome do Controller da API
 */
const controller = "/telao";
/**
 * Classe de gerenciamento de chamadas da API de editor de telão
 *
 * @class
 */
export default class EditorTelaoApi {
  /**
   * Instância da dependência da API (ofetch)
   */
  private api;

  /**
   * Cria uma nova instância da classe EditorTelaoApi.
   * @constructor
   * @param api - Instância da dependência da API (ofetch).
   */
  constructor(api: $Fetch) {
    this.api = api;
  }

  /**
   * Carrega as notícias do RSS para exibição no telão
   * @param localRss - Local do RSS (por exemplo, "G1")
   * @returns Resposta com os dados do RSS
   */
  async carregarRss(localRss: string) {
    try {
      const resp = await this.api(`${controller}/BuscarRss`, {
        method: "GET",
        params: {
          local: localRss,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao carregar o RSS:", error);
      throw error;
    }
  }

  /**
   * Define um tema customizado para o telão
   * @param codTipoSenha - Template do tema a ser definido
   * @returns Resultado da operação
   */
  async gerarSenhaTotem(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/GerarSenhaTotem`, {
        method: "POST",
        params: {
          codTipoSenha,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao definir tema customizado:", error);
      throw error;
    }
  }
  /**
   * Salva o tema customizado do telão no banco de dados
   * @param estruturaTemaBanco - Estrutura do tema a ser salvo
   * @returns Configuração dos dados enviados ou valor padrão se houver erro
   */
  async salvarTemaBanco(estruturaTemaBanco: any) {
    try {
      const resp = await this.api(
        `${controller}/Definir_TemaCustomizadoTelao`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "PUT",
          body: JSON.stringify(estruturaTemaBanco), // Ensure proper serialization
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao definir tema customizado:", error);
      // Don't rethrow the error, handle it here
      return null;
    }
  }

  /**
   * Define um tema customizado para o telão
   * @param templateTema - Template do tema a ser definido
   * @returns Resultado da operação
   */
  async definirTemaCustomizadoTelao(templateTema: object) {
    try {
      const resp = await this.api(
        `${controller}/Definir_TemaCustomizadoTelao`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "PUT",
          body: templateTema,
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao definir tema customizado:", error);
      throw error;
    }
  }

  /**
   * Define um tema customizado para o telão
   * @param templateTema - Template do tema a ser definido
   * @returns Resultado da operação
   */
  async definirVideoStream(templateVideoStream: object) {
    try {
      const resp = await this.api(`${controller}/Definir_Videos_Stream`, {
        method: "PUT",
        body: templateVideoStream,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao definir tema customizado:", error);
      throw error;
    }
  }

  /**
   * Edita um telão existente
   * @param codTelao - Código do telão
   * @param statusTelao - Status do telão
   * @param nomeTelao - Nome do telão
   * @returns Resultado da operação
   */
  async editarTelao(codTelao: number, statusTelao: number, nomeTelao: string) {
    try {
      const resp = await this.api(`${controller}/EditarTelao`, {
        method: "POST",
        body: { codTelao, statusTelao, nomeTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao editar telão:", error);
      throw error;
    }
  }

  /**
   * Lista os telões cadastrados
   * @param codTipoSenha - Código do tipo de senha
   * @returns Lista de telões cadastrados
   */
  async listarTeloesCadastrados(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/ListarTeloesCadastrados`, {
        method: "GET",
        params: {
          codTipoSenha,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao listar telões cadastrados:", error);
      throw error;
    }
  }

  /**
   * Pesquisa o vínculo configurado entre telão e tipo de senha
   * @param codTelao - Código do telão
   * @returns Resultado da operação
   */
  async pesquisarVinculoConfiguradoTelaoTipoSenha(codTelao: number) {
    try {
      const resp = await this.api(
        `${controller}/PesquisarVinculoConfiguradoTelaoTipoSenha`,
        {
          method: "POST",
          body: { codTelao },
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao pesquisar vínculo configurado:", error);
      throw error;
    }
  }

  /**
   * Recupera um tema customizado do telão
   * @returns O tema customizado
   */
  async recuperarTemaBanco() {
    try {
      const resp = await this.api(
        `${controller}/Recuperar_TemaCustomizadoTelao`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao recuperar o tema do banco:", error);
      throw error;
    }
  }

  /**
   * Recupera lista de stream do telão
   * @returns O lsita customizado
   */
  async recuperarListaDeStream() {
    try {
      const resp = await this.api(`${controller}/Lista_Videos_Stream`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      });
      return resp;
    } catch (error) {
      console.error("Erro ao recuperar o tema do banco:", error);
      throw error;
    }
  }

  /**
   * Remove o vínculo por telão
   * @param codTelao - Código do telão
   * @returns Resultado da operação
   */
  async removerVinculoPorTelao(codTelao: number) {
    try {
      const resp = await this.api(`${controller}/RemoverVinculoPorTelao`, {
        method: "POST",
        body: { codTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao remover vínculo por telão:", error);
      throw error;
    }
  }

  /**
   * Remove o vínculo por tipo de senha
   * @param codTipoSenha - Código do tipo de senha
   * @returns Resultado da operação
   */
  async removerVinculoPorTipoSenha(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/RemoverVinculoPorTipoSenha`, {
        method: "POST",
        body: { codTipoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao remover vínculo por tipo de senha:", error);
      throw error;
    }
  }

  /**
   * Inativa um telão
   * @param codTelao - Código do telão
   * @returns Resultado da operação
   */
  async inativarTelao(codTelao: number) {
    try {
      const resp = await this.api(`${controller}/InativarTelao`, {
        method: "POST",
        body: { codTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao inativar telão:", error);
      throw error;
    }
  }

  /**
   * Edita um tipo de senha
   * @param cod - Código do tipo de senha
   * @param status - Status do tipo de senha
   * @param sigla - Sigla do tipo de senha
   * @param descricao - Descrição do tipo de senha
   * @param preferencial - Indica se é preferencial
   * @param grupoSenha - Grupo de senha
   * @returns Resultado da operação
   */
  async editarTipoSenha(
    cod: number,
    status: number,
    sigla: string,
    descricao: string,
    preferencial: number,
    grupoSenha: number,
  ) {
    try {
      const resp = await this.api(`${controller}/EditarTipoSenha`, {
        method: "POST",
        body: { cod, status, sigla, descricao, preferencial, grupoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao editar tipo de senha:", error);
      throw error;
    }
  }

  /**
   * Busca tipo de senha por grupo selecionado
   * @param codGrupoSenha - Código do grupo de senha
   * @returns Tipo de senha
   */
  async tipoSenhaPorGrupoSelecionado(codGrupoSenha: number) {
    try {
      const resp = await this.api(
        `${controller}/TipoSenhaPorGrupoSelecionado`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
          params: {
            codGrupoSenha,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error(
        "Erro ao buscar tipo de senha por grupo selecionado:",
        error,
      );
      throw error;
    }
  }

  /**
   * Inativa um tipo de senha
   * @param codTipoSenha - Código do tipo de senha
   * @returns Resultado da operação
   */
  async inativarTipoSenha(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/InativarTipoSenha`, {
        method: "POST",
        body: { codTipoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao inativar tipo de senha:", error);
      throw error;
    }
  }

  /**
   * Busca tipo de senha cadastrado por grupo
   * @param codGrupoSenha - Código do grupo de senha
   * @returns Tipo de senha cadastrado
   */
  async buscarTipoSenhaCadastradoPorGrupo(codGrupoSenha: number) {
    try {
      const resp = await this.api(
        `${controller}/BuscarTipoSenhaCadastradoPorGrupo`,
        {
          method: "GET",
          params: {
            codGrupoSenha,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error(
        "Erro ao buscar tipo de senha cadastrado por grupo:",
        error,
      );
      throw error;
    }
  }

  /**
   * Lista tipos de senha
   * @param codTelao - Código do telão
   * @returns Lista de tipos de senha
   */
  async listarTipoSenha(codTelao: number) {
    try {
      const resp = await this.api(
        `${controller}/ListarTipoSenha?codTelao=${codTelao}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao listar tipos de senha:", error);
      throw error;
    }
  }

  /**
   * Atualiza um grupo de senha
   * @param objGTS - Objeto grupo de senha
   * @returns Resultado da operação
   */
  async atualizarGrupoSenha(objGTS: object) {
    try {
      const resp = await this.api(`${controller}/AtualizarGrupoSenha`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: objGTS,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao atualizar grupo de senha:", error);
      throw error;
    }
  }

  /**
   * Inativa um grupo de senha
   * @param codGrupoSenha - Código do grupo de senha
   * @returns Resultado da operação
   */
  async inativarGrupoSenha(codGrupoSenha: number) {
    try {
      const resp = await this.api(`${controller}/InativarGrupoSenha`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: { codGrupoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao inativar grupo de senha:", error);
      throw error;
    }
  }

  /**
   * Busca detalhes de grupos de senha selecionados
   * @param arrGrupoSenha - Array de grupos de senha
   * @returns Detalhes dos grupos de senha
   */
  async buscarDetalhesGrupoSenhaSelecionados(arrGrupoSenha: string) {
    try {
      const resp = await this.api(
        `${controller}/BuscarDetalhesGrupoSenhaSelecionados`,
        {
          method: "GET",
          params: {
            arrGrupoSenha,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error(
        "Erro ao buscar detalhes de grupos de senha selecionados:",
        error,
      );
      throw error;
    }
  }

  /**
   * Salva um novo grupo de senha
   * @param objGTS - Objeto grupo de senha
   * @returns Resultado da operação
   */
  async salvarGrupoSenha(objGTS: object) {
    try {
      const resp = await this.api(`${controller}/SalvarGrupoSenha`, {
        method: "POST",
        body: objGTS,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao salvar grupo de senha:", error);
      throw error;
    }
  }

  /**
   * Busca grupos de senha cadastrados
   * @returns Lista de grupos de senha cadastrados
   */
  async buscarGruposSenhaCadastrados() {
    try {
      const resp = await this.api(
        `${controller}/BuscarGruposSenhaCadastrados`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao buscar grupos de senha cadastrados:", error);
      throw error;
    }
  }

  /**
   * Cadastra um novo telão
   * @param objTelao - Objeto telão
   * @returns Resultado da operação
   */
  async cadastrarTelao(objTelao: object) {
    try {
      const resp = await this.api(`${controller}/CadastrarTelao`, {
        method: "POST",
        body: objTelao,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao cadastrar telão:", error);
      throw error;
    }
  }

  /**
   * Cadastra um novo tipo de senha
   * @param sigla - Sigla do tipo de senha
   * @param descricao - Descrição do tipo de senha
   * @param preferencial - Indica se é preferencial
   * @param grupoSenha - Grupo de senha
   * @returns Resultado da operação
   */
  async cadastrarTipoSenha(
    sigla: string,
    descricao: string,
    preferencial: number,
    grupoSenha: number,
  ) {
    try {
      const resp = await this.api(`${controller}/CadastrarTipoSenha`, {
        method: "POST",
        body: { sigla, descricao, preferencial, grupoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao cadastrar tipo de senha:", error);
      throw error;
    }
  }

  /**
   * Lista tipos de senha escolhidos para o totem
   * @param listaTipos - Lista de tipos de senha
   * @returns Lista de tipos de senha escolhidos
   */
  async listarTipoSenhaEscolhidosTotem(listaTipos: string) {
    try {
      const resp = await this.api(
        `${controller}/ListarTipoSenhaEscolhidosTotem`,
        {
          method: "GET",
          params: {
            listaTipos,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error(
        "Erro ao listar tipos de senha escolhidos para o totem:",
        error,
      );
      throw error;
    }
  }

  /**
   * Recupera o cabeçalho do totem de senha
   * @returns Cabeçalho do totem
   */
  async recuperarCabecalhoTotemSenha() {
    try {
      const resp = await this.api(
        `${controller}/RecuperarCabecalhoTotemSenha`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao recuperar cabeçalho do totem de senha:", error);
      throw error;
    }
  }

  /**
   * Recupera o rodapé do totem de senha
   * @returns Rodapé do totem
   */
  async recuperarRodapeTotemSenha() {
    try {
      const resp = await this.api(`${controller}/RecuperarRodapeTotemSenha`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      });
      return resp;
    } catch (error) {
      console.error("Erro ao recuperar rodapé do totem de senha:", error);
      throw error;
    }
  }

  /**
   * Vincula um telão a um tipo de senha
   * @param codTelao - Código do telão
   * @param codTipoSenha - Código do tipo de senha
   * @returns Resultado da operação
   */
  async vincularTelaoTipoSenha(codTelao: number, codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/VincularTelaoTipoSenha`, {
        method: "POST",
        body: { codTelao, codTipoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao vincular telão a tipo de senha:", error);
      throw error;
    }
  }

  /**
   * Vincula um tipo de senha a um telão
   * @param codTipoSenha - Código do tipo de senha
   * @param codTelao - Código do telão
   * @returns Resultado da operação
   */
  async vincularTipoSenhaTelao(codTipoSenha: number, codTelao: number) {
    try {
      const resp = await this.api(`${controller}/VincularTipoSenhaTelao`, {
        method: "POST",
        body: { codTipoSenha, codTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao vincular tipo de senha a telão:", error);
      throw error;
    }
  }

  /**
   * Valida o acesso
   * @returns Token de permissão
   */
  async validarAcesso() {
    try {
      const resp = await this.api(`${controller}/ValidarAcesso`, {
        method: "GET",
      });
      return resp;
    } catch (error) {
      console.error("Erro ao validar acesso:", error);
      throw error;
    }
  }
}
